import { Building2, MapPin, Store, ChevronRight, Users, TrendingUp, Loader } from 'lucide-react';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';
import { Button } from '../ui/Button';
import type { BusinessUnitType, BusinessUnitQuickStats } from '../../types/business-unit';
import { useState, useEffect } from 'react';

/**
 * Labels et icônes pour chaque type d'unité
 */
const UNIT_CONFIG: Record<BusinessUnitType, { icon: React.ElementType; color: string }> = {
  company: { icon: Building2, color: 'text-blue-500' },
  branch: { icon: MapPin, color: 'text-green-500' },
  pos: { icon: Store, color: 'text-purple-500' }
};

interface BusinessUnitBannerProps {
  showChangeButton?: boolean;
  showStats?: boolean;
}

/**
 * Bannière affichant le niveau actuel de filtrage des données
 * Similaire à FiscalYearBanner mais pour le périmètre organisationnel
 * Avec stats rapides via l'endpoint /business-units/:id/stats
 */
export function BusinessUnitBanner({ showChangeButton = true, showStats = true }: BusinessUnitBannerProps) {
  const {
    selectedUnit,
    selectedUnitPath,
    currentLevelLabel,
    hierarchy,
    includeChildren,
    isCompanyLevel,
    isBranchLevel,
    isPosLevel,
    fetchStats
  } = useBusinessUnit();
  
  // État pour les statistiques rapides de l'API
  const [stats, setStats] = useState<BusinessUnitQuickStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Charger les stats quand l'unité sélectionnée change
  useEffect(() => {
    async function loadStats() {
      if (!selectedUnit || !showStats) return;
      
      setStatsLoading(true);
      try {
        const result = await fetchStats(selectedUnit.id);
        setStats(result);
      } catch (error) {
        console.error('Erreur chargement stats:', error);
      } finally {
        setStatsLoading(false);
      }
    }
    
    loadStats();
  }, [selectedUnit?.id, showStats, fetchStats]);
  
  if (!selectedUnit || !hierarchy) {
    return null;
  }
  
  const config = UNIT_CONFIG[selectedUnit.type as BusinessUnitType] || UNIT_CONFIG.company;
  const Icon = config.icon;
  
  // Utiliser les stats API si disponibles, sinon fallback sur la hiérarchie locale
  const branches = Array.isArray(hierarchy.branches) ? hierarchy.branches : [];
  const pointsOfSale = Array.isArray(hierarchy.pointsOfSale) ? hierarchy.pointsOfSale : [];
  
  const branchCount = stats?.branchCount ?? branches.length;
  const posCount = stats?.posCount ?? pointsOfSale.length;
  const activeChildCount = stats?.activeChildCount ?? 0;
  
  let scopeDescription = '';
  if (isCompanyLevel()) {
    scopeDescription = `${branchCount} succursale${branchCount > 1 ? 's' : ''} • ${posCount} point${posCount > 1 ? 's' : ''} de vente`;
  } else if (isBranchLevel()) {
    const branchPosCount = stats?.posCount ?? pointsOfSale.filter(
      pos => pos.branchId === selectedUnit.id
    ).length;
    scopeDescription = `${branchPosCount} point${branchPosCount > 1 ? 's' : ''} de vente`;
  }
  
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Icône du niveau */}
          <div className={`p-2 rounded-lg bg-background ${config.color}`}>
            <Icon className="h-5 w-5" />
          </div>
          
          {/* Informations */}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Périmètre de données:
              </span>
              <span className="font-semibold text-foreground">
                {currentLevelLabel}
              </span>
            </div>
            
            <div className="flex items-center gap-1 text-sm mt-0.5">
              {/* Breadcrumb du chemin */}
              <span className="font-medium text-primary">
                {selectedUnitPath}
              </span>
              
              {includeChildren && !isPosLevel() && (
                <span className="text-muted-foreground ml-2">
                  (données consolidées)
                </span>
              )}
            </div>
            
            {scopeDescription && (
              <p className="text-xs text-muted-foreground mt-1">
                {statsLoading ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader className="h-3 w-3 animate-spin" />
                    Chargement...
                  </span>
                ) : (
                  <>
                    {scopeDescription}
                    {stats && activeChildCount > 0 && !isPosLevel() && (
                      <span className="ml-2 text-green-600 dark:text-green-400">
                        • {activeChildCount} actif{activeChildCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        
        {/* Indicateurs rapides (si stats chargées) */}
        {showStats && stats && !isPosLevel() && (
          <div className="hidden sm:flex items-center gap-4 mr-4">
            {isCompanyLevel() && stats.branchCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs">
                <MapPin className="h-3.5 w-3.5 text-green-500" />
                <span className="text-muted-foreground">{stats.branchCount}</span>
              </div>
            )}
            {stats.posCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs">
                <Store className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-muted-foreground">{stats.posCount}</span>
              </div>
            )}
            {stats.childCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs">
                <Users className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-muted-foreground">{stats.childCount} sous-unités</span>
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        {showChangeButton && (
          <div className="flex items-center gap-2">
            {!isCompanyLevel() && (
              <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                Filtré
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Version compacte de la bannière pour les en-têtes de page
 */
export function BusinessUnitBreadcrumb() {
  const { hierarchy, selectedUnit, selectedUnitPath } = useBusinessUnit();
  
  if (!hierarchy || !selectedUnit) return null;
  
  const parts = selectedUnitPath.split(' > ');
  
  return (
    <div className="flex items-center gap-1 text-sm text-muted-foreground">
      {parts.map((part, index) => (
        <div key={index} className="flex items-center gap-1">
          {index > 0 && <ChevronRight className="h-3 w-3" />}
          <span className={index === parts.length - 1 ? 'font-medium text-foreground' : ''}>
            {part}
          </span>
        </div>
      ))}
    </div>
  );
}
